
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Unlock, ShieldCheck, Mail, Lock } from 'lucide-react';
import { playSound } from '../services/audioService';
import { toast } from 'react-hot-toast';
import { getFunctionsBaseUrl, supabase } from '../services/supabase';

interface Props {
    onUnlock: () => void;
}

/**
 * v23.3: LockScreen (NPM-Powered)
 * Ajustado para autenticação via Supabase.
 */
export const LockScreen: React.FC<Props> = ({ onUnlock }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(false);

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(false);

        try {
            const normalizedEmail = email.trim().toLowerCase();
            const { error: signInError } = await supabase.auth.signInWithPassword({
                email: normalizedEmail,
                password
            });

            if (signInError) {
                const migrateUrl = `${getFunctionsBaseUrl()}/sal-auth-migrate`;
                const response = await fetch(migrateUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: normalizedEmail, password })
                });

                if (!response.ok) throw new Error('Falha na migração');

                const retry = await supabase.auth.signInWithPassword({
                    email: normalizedEmail,
                    password
                });
                if (retry.error) throw retry.error;
            }

            playSound('success');
        } catch (err) {
            console.error(err);
            setError(true);
            playSound('error');
            toast.error("Credenciais inválidas ou provedor desativado.");
            setTimeout(() => setError(false), 500);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-app-bg flex flex-col items-center justify-center p-4 relative overflow-hidden transition-colors duration-300">
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.03] dark:opacity-20 pointer-events-none"></div>

            <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1, x: error ? [0, -10, 10, -10, 10, 0] : 0 }}
                style={{
                    backgroundColor: 'rgba(var(--glass-panel-bg))',
                    backdropFilter: 'blur(32px)',
                    WebkitBackdropFilter: 'blur(32px)'
                }}
                className="z-10 w-full max-w-[380px] border border-border-dim p-8 rounded-[2.5rem] shadow-[var(--shadow-lg)] relative"
            >
                <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-card-bg p-4 rounded-3xl border border-border-dim shadow-xl">
                    <ShieldCheck className="w-10 h-10 text-sal-cyber-teal" />
                </div>

                <div className="mt-8 text-center mb-8">
                    <h1 className="text-3xl font-black text-text-primary tracking-tighter uppercase mb-1">S.A.L. Rota</h1>
                    <p className="text-xs text-text-muted uppercase tracking-[0.3em] font-black">Acesso em Nuvem SAAS</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                        <div className="relative">
                            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-icon-muted" aria-hidden="true" />
                            <label htmlFor="email-input" className="sr-only">Endereço de e-mail</label>
                            <input
                                id="email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-input-bg border border-border-dim pl-12 pr-4 py-4 rounded-2xl text-base focus:border-sal-cyber-teal transition-all text-text-primary placeholder:text-text-muted/50"
                                placeholder="E-mail do Gestor"
                                required
                                disabled={isLoading}
                                name="email"
                                autoComplete="email"
                                aria-label="Endereço de e-mail"
                            />
                        </div>
                        <div className="relative">
                            <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-icon-muted" aria-hidden="true" />
                            <label htmlFor="password-input" className="sr-only">Senha de acesso</label>
                            <input
                                id="password-input"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-input-bg border border-border-dim pl-12 pr-4 py-4 rounded-2xl text-base focus:border-sal-cyber-teal transition-all text-text-primary placeholder:text-text-muted/50"
                                placeholder="Senha de Acesso"
                                required
                                disabled={isLoading}
                                name="password"
                                autoComplete="current-password"
                                aria-label="Senha de acesso"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="w-full bg-sal-cyber-teal text-badge-text-inv font-black py-4 rounded-2xl shadow-xl shadow-cyan-900/20 hover:brightness-110 active:scale-95 transition-all uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {isLoading ? "Autenticando..." : <><Unlock className="w-4 h-4" /> Entrar no Sistema</>}
                    </button>
                </form>

                <div className="mt-8 text-center">
                    <p className="text-xs text-text-muted font-bold uppercase leading-relaxed tracking-wider">
                        Sistema Distribuído em Nuvem.<br />
                        <span className="opacity-50">Acesso monitorado via Supabase Shield</span>
                    </p>
                </div>
            </motion.div>
        </div>
    );
};
